/**
 * Microsoft 365 einrichten.
 *
 * Warum das hier steht und nicht nur in der `.env` auf dem Server: ein
 * Klientgeheimnis läuft ab. Steht es ausschließlich auf dem Server, steht
 * der Mailversand so lange, bis jemand über SSH herangeht.
 *
 * Drei Dinge dazu, die man beim Lesen wissen sollte:
 *
 *  - Das Geheimnis wird verschlüsselt abgelegt und kommt nie wieder heraus.
 *    Das Feld ist deshalb immer leer; leer lassen heißt "unverändert".
 *  - Was hier eingetragen wird, hat Vorrang vor der `.env`. Bei jedem Feld
 *    steht, woher der geltende Wert stammt.
 *  - Die Anleitung steht daneben. Wer im Entra-Portal sitzt, soll nicht in
 *    einer Textdatei auf dem Server nachschlagen müssen.
 */

import { useEffect, useState } from 'react'
import {
  useMicrosoft,
  useMicrosoftProbe,
  useMicrosoftSetzen,
  useMicrosoftTestmail,
  type EinstellungStand,
} from '../api/verwaltung'
import { Fehlerhinweis, Ladeanzeige } from '../bausteine/Hinweise'

const QUELLENTEXT: Record<EinstellungStand['quelle'], string> = {
  datenbank: 'hier eingetragen',
  umgebung: 'aus der .env auf dem Server',
  fehlt: 'nicht gesetzt',
}

export function MicrosoftBereich() {
  const stand = useMicrosoft()
  const speichern = useMicrosoftSetzen()
  const probe = useMicrosoftProbe()
  const testmail = useMicrosoftTestmail()

  const [mandant, setzeMandant] = useState('')
  const [klientId, setzeKlientId] = useState('')
  const [geheimnis, setzeGeheimnis] = useState('')
  const [absender, setzeAbsender] = useState('')
  const [gespeichert, setzeGespeichert] = useState(false)

  // Die sichtbaren Werte aus der Antwort übernehmen. Das Geheimnisfeld bleibt
  // absichtlich leer – es kommt nicht zurück.
  useEffect(() => {
    if (!stand.data) return
    setzeMandant(stand.data.felder.ms_mandant?.wert ?? '')
    setzeKlientId(stand.data.felder.ms_klient_id?.wert ?? '')
    setzeAbsender(stand.data.felder.mail_absender?.wert ?? '')
  }, [stand.data])

  if (stand.isLoading) return <Ladeanzeige />
  if (stand.error) return <Fehlerhinweis fehler={stand.error} />

  const daten = stand.data!
  const geheimGesetzt = daten.felder.ms_geheimnis?.gesetzt ?? false
  const geheimLesbar = daten.felder.ms_geheimnis?.lesbar ?? true

  return (
    <div className="space-y-4">
      <Zustandsleiste
        anmeldung={daten.anmeldung_moeglich}
        versand={daten.versand_moeglich}
      />

      {!geheimLesbar && (
        <div
          role="alert"
          className="rounded-feld border-2 border-stufe-3 bg-stufe-3-grund p-3 text-stufe-3"
        >
          <p className="font-semibold">Das abgelegte Geheimnis ist unlesbar.</p>
          <p className="mt-1">
            Das passiert, wenn der Serverschlüssel (SPUER_GEHEIMNIS) ausgetauscht
            wurde. Bitte im Entra-Portal ein neues Geheimnis erzeugen und unten
            eintragen.
          </p>
        </div>
      )}

      <form
        className="karte space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          setzeGespeichert(false)
          // Nur mitschicken, was gefüllt ist oder sich geändert hat. Ein leer
          // gelassenes Geheimnisfeld darf das vorhandene nicht löschen.
          const nutzlast: Record<string, string> = {
            ms_mandant: mandant.trim(),
            ms_klient_id: klientId.trim(),
            mail_absender: absender.trim(),
          }
          if (geheimnis.trim()) nutzlast.ms_geheimnis = geheimnis.trim()

          speichern.mutate(nutzlast, {
            onSuccess: () => {
              setzeGeheimnis('')
              setzeGespeichert(true)
            },
          })
        }}
      >
        <h2 className="text-xl font-bold">Zugangsdaten</h2>

        <Feld
          kennung="ms-mandant"
          beschriftung="Verzeichnis-ID (Mandant)"
          wert={mandant}
          setzen={setzeMandant}
          stand={daten.felder.ms_mandant}
          hinweis={
            'Die Verzeichnis-ID aus dem Entra-Portal, nicht „common". Sonst ' +
            'könnte sich jedes Microsoft-Konto der Welt vorstellen.'
          }
        />

        <Feld
          kennung="ms-klient"
          beschriftung="Anwendungs-ID (Klient)"
          wert={klientId}
          setzen={setzeKlientId}
          stand={daten.felder.ms_klient_id}
        />

        <div>
          <label htmlFor="ms-geheimnis">
            Klientgeheimnis{' '}
            <span className="font-normal text-slate-600">
              {geheimGesetzt ? '· gesetzt' : '· fehlt'}
            </span>
          </label>
          <input
            id="ms-geheimnis"
            type="password"
            value={geheimnis}
            onChange={(e) => setzeGeheimnis(e.target.value)}
            autoComplete="off"
            placeholder={geheimGesetzt ? 'leer lassen = unverändert' : 'aus dem Entra-Portal'}
          />
          <p className="mt-1 text-base text-slate-600">
            Wird verschlüsselt abgelegt und nie wieder angezeigt – auch nicht hier.
            Im Entra-Portal ist der Wert ebenfalls nur einmal sichtbar.
          </p>
        </div>

        <Feld
          kennung="ms-absender"
          beschriftung="Absenderpostfach"
          wert={absender}
          setzen={setzeAbsender}
          stand={daten.felder.mail_absender}
          typ="email"
          hinweis="Ein gemeinsames Postfach wie pruefung@spuer.net ist besser als ein persönliches."
        />

        <Fehlerhinweis fehler={speichern.error} />

        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" className="knopf-haupt" disabled={speichern.isPending}>
            {speichern.isPending ? 'Speichert …' : 'Speichern'}
          </button>
          {gespeichert && (
            <span role="status" className="marke bg-stufe-ob-grund text-stufe-ob">
              Gespeichert
            </span>
          )}
        </div>
      </form>

      <div className="karte space-y-3">
        <h2 className="text-xl font-bold">Prüfen</h2>
        <p className="text-base text-slate-700">
          Die Prüfung holt einen Zugangsnachweis und liest das Absenderpostfach –
          <span className="font-semibold"> ohne eine Mail zu verschicken</span>. Damit
          lässt sich Einrichtung von Zustellung trennen.
        </p>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="knopf-zweit"
            onClick={() => probe.mutate()}
            disabled={probe.isPending}
          >
            {probe.isPending ? 'Prüft …' : 'Zugang prüfen'}
          </button>
          <button
            type="button"
            className="knopf-still"
            onClick={() => testmail.mutate('kein')}
            disabled={testmail.isPending || !daten.versand_moeglich}
            title={
              daten.versand_moeglich
                ? 'Schickt eine Nachricht an Ihre eigene Adresse'
                : 'Erst Zugangsdaten und Absenderpostfach eintragen'
            }
          >
            {testmail.isPending ? 'Verschickt …' : 'Testmail an mich'}
          </button>
          {/* Für Anhänge gibt es zwei Wege, und nur der kurze fällt ohne
              weiteres auf. Der lange greift erst über 3 MB – also genau bei
              einem Prüfbericht mit Fotos. Ihn erst beim ersten echten
              Bericht zu erproben hieße, ihn beim Kunden zu erproben. */}
          <button
            type="button"
            className="knopf-still"
            onClick={() => testmail.mutate('klein')}
            disabled={testmail.isPending || !daten.versand_moeglich}
            title="Mit einem kleinen PDF im Anhang"
          >
            … mit Anhang
          </button>
          <button
            type="button"
            className="knopf-still"
            onClick={() => testmail.mutate('gross')}
            disabled={testmail.isPending || !daten.versand_moeglich}
            title="Mit einem 3-MB-PDF – prüft den Weg, den Prüfberichte mit Fotos nehmen"
          >
            … mit großem Anhang
          </button>
        </div>
        <p className="mt-2 text-base text-slate-600">
          Der große Anhang prüft den Weg, den ein Prüfbericht mit Fotos nimmt.
          Microsoft nimmt eine fertige Nachricht nur bis 4 MB an; darüber wird
          der Anhang in Stücken hochgeladen.
        </p>

        <Meldung ergebnis={probe.data} fehler={probe.error} />
        <Meldung ergebnis={testmail.data} fehler={testmail.error} />
      </div>

      <Anleitung rueckkehrUrl={daten.rueckkehr_url} />
    </div>
  )
}

function Zustandsleiste({ anmeldung, versand }: { anmeldung: boolean; versand: boolean }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Zustand
        an={anmeldung}
        titel="Anmeldung über Microsoft"
        jaText="Der Knopf erscheint auf der Anmeldeseite."
        neinText="Verzeichnis-ID, Anwendungs-ID und Geheimnis fehlen noch."
      />
      <Zustand
        an={versand}
        titel="Mailversand über Graph"
        jaText="Erinnerungen und Bestätigungslinks gehen als Mail raus."
        neinText="Ohne Absenderpostfach wird nur ins Protokoll geschrieben."
      />
    </div>
  )
}

function Zustand({
  an,
  titel,
  jaText,
  neinText,
}: {
  an: boolean
  titel: string
  jaText: string
  neinText: string
}) {
  return (
    <div
      className={`karte ${an ? 'border-stufe-ob bg-stufe-ob-grund' : 'border-stufe-2 bg-stufe-2-grund'}`}
    >
      <div className={`font-bold ${an ? 'text-stufe-ob' : 'text-stufe-2'}`}>
        {titel}: {an ? 'eingerichtet' : 'nicht eingerichtet'}
      </div>
      <p className="mt-1 text-base text-slate-700">{an ? jaText : neinText}</p>
    </div>
  )
}

function Feld({
  kennung,
  beschriftung,
  wert,
  setzen,
  stand,
  hinweis,
  typ = 'text',
}: {
  kennung: string
  beschriftung: string
  wert: string
  setzen: (w: string) => void
  stand?: EinstellungStand
  hinweis?: string
  typ?: string
}) {
  return (
    <div>
      <label htmlFor={kennung}>
        {beschriftung}
        {stand && (
          <span className="font-normal text-slate-600"> · {QUELLENTEXT[stand.quelle]}</span>
        )}
      </label>
      <input
        id={kennung}
        type={typ}
        value={wert}
        onChange={(e) => setzen(e.target.value)}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
      />
      {hinweis && <p className="mt-1 text-base text-slate-600">{hinweis}</p>}
    </div>
  )
}

function Meldung({
  ergebnis,
  fehler,
}: {
  ergebnis?: { erfolg: boolean; meldung: string }
  fehler: unknown
}) {
  if (fehler) return <Fehlerhinweis fehler={fehler} />
  if (!ergebnis) return null

  return (
    <p
      role="status"
      className={`rounded-feld border-2 p-3 ${
        ergebnis.erfolg
          ? 'border-stufe-ob bg-stufe-ob-grund text-stufe-ob'
          : 'border-stufe-3 bg-stufe-3-grund text-stufe-3'
      }`}
    >
      {ergebnis.meldung}
    </p>
  )
}

function Anleitung({ rueckkehrUrl }: { rueckkehrUrl: string }) {
  const [offen, setzeOffen] = useState(false)

  return (
    <div className="karte">
      <button
        type="button"
        onClick={() => setzeOffen((o) => !o)}
        className="flex w-full items-center justify-between text-left text-xl font-bold"
        aria-expanded={offen}
      >
        Einrichtung im Entra-Portal
        <span aria-hidden className="text-2xl">
          {offen ? '−' : '+'}
        </span>
      </button>

      {offen && (
        <ol className="mt-3 list-decimal space-y-3 pl-5">
          <li>
            <span className="font-semibold">App registrieren.</span> entra.microsoft.com →
            Identität → Anwendungen → App-Registrierungen → Neue Registrierung. Name
            „SPÜR Prüfmanager", Kontotypen: nur dieses Verzeichnis. Als Umleitungs-URI
            (Web) genau das hier eintragen:
            <code className="mt-1 block overflow-x-auto rounded-feld bg-slate-100 p-2 text-base">
              {rueckkehrUrl}
            </code>
          </li>
          <li>
            <span className="font-semibold">Geheimnis erzeugen.</span> Zertifikate &amp;
            Geheimnisse → neuer geheimer Clientschlüssel. Der Wert ist nur einmal
            sichtbar – sofort oben eintragen.
          </li>
          <li>
            <span className="font-semibold">Berechtigungen.</span> API-Berechtigungen →
            Microsoft Graph:
            <ul className="mt-1 list-disc pl-5">
              <li>Delegiert: openid, email, profile — für die Anmeldung</li>
              <li>Anwendung: Mail.Send — für den Versand</li>
              <li>Anwendung: User.Read.All — nur für die Zugangsprüfung</li>
            </ul>
            Danach <span className="font-semibold">Administratorzustimmung erteilen</span>{' '}
            anklicken, sonst gilt nichts davon.
          </li>
          <li>
            <span className="font-semibold">Versand eingrenzen.</span> Mail.Send als
            Anwendungsberechtigung erlaubt sonst das Senden als{' '}
            <span className="font-semibold">jedes</span> Postfach eures Mandanten. In der
            Exchange-PowerShell auf das eine Postfach begrenzen:
            <code className="mt-1 block overflow-x-auto rounded-feld bg-slate-100 p-2 text-base">
              New-ApplicationAccessPolicy -AppId &lt;Anwendungs-ID&gt;
              -PolicyScopeGroupId &lt;Absenderpostfach&gt; -AccessRight RestrictAccess
              -Description "SPUER Pruefmanager"
            </code>
          </li>
          <li>
            <span className="font-semibold">Prüfen.</span> Oben auf „Zugang prüfen". Geht
            das durch, stimmen Zugang und Berechtigung.
          </li>
        </ol>
      )}
    </div>
  )
}
